import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useLanguage } from '@context'
import { GraphData, GraphNode } from '@hooks'

const NODE_COLORS: Record<string, string> = {
  ME: '#444444',
  PRIVATE: '#5DA53B',
  PROFESSIONAL: '#2E78B8',
  ghost: '#999999',
  category: '#CF874B',
  hashtag: '#9C27B0',
  profession: '#E91E63',
  nationality: '#D39E50',
  city: '#00ACC1',
  language: '#3F51B5',
}

const EDGE_COLORS: Record<string, string> = {
  contact: '#2E78B8',
  category: '#CF874B',
  hashtag: '#9C27B0',
  profession: '#E91E63',
  nationality: '#D39E50',
  city: '#00ACC1',
  language: '#3F51B5',
}

interface ForceGraphProps {
  graphData: GraphData
  highlightedNodeId?: string | null
  onNodeClick?: (node: GraphNode) => void
  width: number
  height: number
}

const imageCache = new Map<string, HTMLImageElement>()

const loadImage = (url: string): Promise<HTMLImageElement> => {
  if (imageCache.has(url)) return Promise.resolve(imageCache.get(url)!)
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      imageCache.set(url, img)
      resolve(img)
    }
    img.onerror = () => resolve(img)
    img.src = url
  })
}

export const ForceGraphCanvas: React.FC<ForceGraphProps> = ({
  graphData,
  highlightedNodeId,
  onNodeClick,
  width,
  height,
}) => {
  const { t } = useLanguage()
  const [ForceGraph2D, setForceGraph2D] = useState<any>(null)
  const fgRef = useRef<any>(null)
  const [loadedImages, setLoadedImages] = useState<Map<string, HTMLImageElement>>(new Map())

  useEffect(() => {
    import('react-force-graph-2d').then((mod) => setForceGraph2D(() => mod.default))
  }, [])

  // Preload avatars
  useEffect(() => {
    const urls = graphData.nodes
      .map((n) => n.avatarUrl)
      .filter((url): url is string => !!url)
    Promise.all(urls.map(loadImage)).then(() => {
      setLoadedImages(new Map(imageCache))
    })
  }, [graphData.nodes])

  const getNodeColor = useCallback((node: any) => {
    if (node.isGhost) return NODE_COLORS.ghost
    if (node.type !== 'contact' && node.type !== 'ghost') return node.color || NODE_COLORS[node.type] || '#ccc'
    return NODE_COLORS[node.scope] || NODE_COLORS.PRIVATE
  }, [])

  const getNodeSize = useCallback((node: any) => {
    if (node.scope === 'ME') return 10
    if (node.type === 'contact' || node.type === 'ghost') return 6
    return 4
  }, [])

  const drawNode = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const isPersonNode = node.type === 'contact' || node.type === 'ghost'
    const radius = getNodeSize(node)
    const isHighlighted = highlightedNodeId === node.id

    // Highlight ring
    if (isHighlighted) {
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius + 3, 0, 2 * Math.PI)
      ctx.fillStyle = 'rgba(255, 215, 0, 0.4)'
      ctx.fill()
      ctx.strokeStyle = '#FFD700'
      ctx.lineWidth = 2
      ctx.stroke()
    }

    // Node circle
    ctx.beginPath()
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI)
    ctx.fillStyle = getNodeColor(node)
    ctx.fill()

    // Border
    ctx.strokeStyle = isHighlighted ? '#FFD700' : 'rgba(255,255,255,0.8)'
    ctx.lineWidth = isPersonNode ? 1.5 : 0.5
    ctx.stroke()

    // Avatar for person nodes
    if (isPersonNode && node.avatarUrl && loadedImages.has(node.avatarUrl)) {
      const img = loadedImages.get(node.avatarUrl)!
      ctx.save()
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius - 1, 0, 2 * Math.PI)
      ctx.clip()
      ctx.drawImage(img, node.x - radius + 1, node.y - radius + 1, (radius - 1) * 2, (radius - 1) * 2)
      ctx.restore()
    }

    // Label — hidden when zoomed far out (avoids an unreadable wall of text);
    // drawn over a soft white halo so it stays legible if two labels cross.
    const showLabel = isPersonNode ? globalScale > 0.55 : globalScale > 1.1
    if (showLabel) {
      const fontSize = isPersonNode ? Math.max(10 / globalScale, 2.5) : Math.max(8 / globalScale, 2)
      ctx.font = `${isPersonNode ? 'bold ' : ''}${fontSize}px sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      const labelY = node.y + radius + 2
      ctx.lineWidth = Math.max(2.5 / globalScale, 0.6)
      ctx.strokeStyle = 'rgba(255,255,255,0.9)'
      ctx.lineJoin = 'round'
      ctx.strokeText(node.label, node.x, labelY)
      ctx.fillStyle = isPersonNode ? '#333' : (node.color || '#666')
      ctx.fillText(node.label, node.x, labelY)
    }
  }, [highlightedNodeId, loadedImages, getNodeColor, getNodeSize])

  const fgData = useMemo(() => ({
    nodes: graphData.nodes.map((n) => ({ ...n })),
    links: graphData.edges.map((e) => ({ ...e })),
  }), [graphData])

  // Tune the layout so the graph reads clearly: stronger repulsion spreads the
  // nodes out, longer links separate connected nodes, and a collision force
  // keeps bubbles + their labels from overlapping.
  useEffect(() => {
    const fg = fgRef.current
    if (!fg || !ForceGraph2D) return
    let cancelled = false
    // d3-force-3d is what react-force-graph uses internally; reuse its forces.
    import('d3-force-3d').then(({ forceCollide, forceX, forceY }) => {
      if (cancelled) return
      // Moderate repulsion + collision: connected nodes spread without piling up.
      fg.d3Force('charge')?.strength(-130).distanceMax(300)
      fg.d3Force('link')?.distance((l: any) => (l.type === 'contact' ? 80 : 55)).strength(0.3)
      fg.d3Force('collide', forceCollide((n: any) => getNodeSize(n) + 14))
      // Gentle pull toward the centre so unlinked nodes don't drift to the edges.
      fg.d3Force('x', forceX(0).strength(0.06))
      fg.d3Force('y', forceY(0).strength(0.06))
      fg.d3ReheatSimulation?.()
    })
    return () => {
      cancelled = true
    }
  }, [ForceGraph2D, fgData, getNodeSize])

  if (!ForceGraph2D) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width,
          height,
          color: '#999',
        }}
      >
        {t('graphScreen.loadingGraph')}
      </div>
    )
  }

  return (
    <ForceGraph2D
      ref={fgRef}
      graphData={fgData}
      width={width}
      height={height}
      nodeCanvasObject={drawNode}
      nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
        const r = getNodeSize(node) + 2
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(node.x, node.y, r, 0, 2 * Math.PI)
        ctx.fill()
      }}
      linkColor={(link: any) => EDGE_COLORS[link.type] || '#ddd'}
      linkWidth={(link: any) => (link.type === 'contact' ? 1.5 : 0.5)}
      linkDirectionalParticles={0}
      onNodeClick={(node: any) => onNodeClick?.(node as GraphNode)}
      onEngineStop={() => fgRef.current?.zoomToFit?.(500, 70)}
      cooldownTicks={200}
      d3AlphaDecay={0.025}
      d3VelocityDecay={0.35}
      enableNodeDrag
      enableZoomInteraction
      enablePanInteraction
    />
  )
}
